import { ModifierGroupData, PreModifierData, PreModifierOptionData, PriceOptionData } from '../models';
import {
  OrderedItemModifier,
  OrderedItemModifierPreModifierOption,
} from '../interfaces/ordering/order.interfaces';

export interface ResolvedPreModifierSelection {
  preModifier: PreModifierData;
  option: PreModifierOptionData;
  optionId: string;
}

export interface ModifierPricingResult {
  unitPrice: number;
  taxPerUnit: number;
  preTaxPerUnit: number;
  taxIncluded: boolean;
  priceAdjustmentPerUnit: number;
}

function normalizeNumber(value: any, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function coercePreModifierData(
  preModifier: string | PreModifierData | null | undefined,
  preModifiersById?: Record<string, PreModifierData>
): PreModifierData | null {
  if (!preModifier) {
    return null;
  }

  if (typeof preModifier === 'object') {
    return preModifier as PreModifierData;
  }

  if (preModifiersById?.[preModifier]) {
    return preModifiersById[preModifier];
  }

  return null;
}

function getPreModifierOptionId(modifier: Partial<OrderedItemModifier> & Record<string, any>): string | undefined {
  const option = modifier.preModifierOption as
    | string
    | null
    | undefined
    | OrderedItemModifierPreModifierOption
    | { optionId?: string; _id?: string };

  if (typeof option === 'string' && option.length) {
    return option;
  }

  if (option && typeof option === 'object') {
    if ('optionId' in option && option.optionId) {
      return option.optionId.toString();
    }

    if ('_id' in option && option._id) {
      return option._id.toString();
    }
  }

  if (modifier.preModifierOptionId) {
    return modifier.preModifierOptionId.toString();
  }

  return undefined;
}

export function resolvePreModifierSelection(
  modifier: Partial<OrderedItemModifier>,
  modifierGroup: ModifierGroupData,
  preModifiersById: Record<string, PreModifierData> = {}
): ResolvedPreModifierSelection | null {
  const groupPreModifier = coercePreModifierData(modifierGroup.preModifier, preModifiersById);
  const explicitPreModifier = coercePreModifierData(modifier.preModifier, preModifiersById);

  const resolvedPreModifier = explicitPreModifier || groupPreModifier;

  if (!resolvedPreModifier) {
    return null;
  }

  const optionIdFromSelection = getPreModifierOptionId(modifier as Record<string, any>);
  const optionIdFromDefault = resolvedPreModifier.options?.find(opt => opt.isDefault)?._id;
  const optionId = optionIdFromSelection || optionIdFromDefault;

  if (!optionId) {
    return null;
  }

  const normalizedOptionId = optionId.toString();
  const option = resolvedPreModifier.options?.find(opt => opt._id?.toString() === normalizedOptionId);

  if (!option) {
    return null;
  }

  return {
    preModifier: resolvedPreModifier,
    option,
    optionId: normalizedOptionId,
  };
}

export function calculateModifierPricing(
  priceOption: PriceOptionData,
  selection: ResolvedPreModifierSelection | null
): ModifierPricingResult {
  const taxIncluded = !!priceOption?.taxIncluded;
  const basePrice = normalizeNumber(priceOption?.price);
  const baseTaxAmount = normalizeNumber(priceOption?.taxAmount);
  const basePreTaxPrice = normalizeNumber(
    priceOption?.preTaxPrice,
    taxIncluded ? basePrice - baseTaxAmount : basePrice
  );

  let perUnitPreTax = basePreTaxPrice;
  let perUnitTax = baseTaxAmount;
  let priceAdjustmentPerUnit = 0;

  if (selection) {
    const option = selection.option;
    const multiplyPrice = !!option.multiplyPrice;
    const multiplicationFactor = normalizeNumber(option.multiplicationFactor ?? 1, 1);
    const fixedPrice = normalizeNumber(option.fixedPrice);

    if (multiplyPrice) {
      perUnitPreTax = Math.round(perUnitPreTax * multiplicationFactor);
      perUnitTax = Math.round(perUnitTax * multiplicationFactor);
    } else if (fixedPrice) {
      const taxRate = basePreTaxPrice > 0 ? baseTaxAmount / basePreTaxPrice : 0;
      if (taxIncluded) {
        const gross = fixedPrice;
        perUnitPreTax = taxRate > 0 ? Math.round(gross / (1 + taxRate)) : gross;
        perUnitTax = gross - perUnitPreTax;
      } else {
        perUnitPreTax = fixedPrice;
        perUnitTax = Math.round(perUnitPreTax * taxRate);
      }
    }

    if (perUnitPreTax < 0) {
      perUnitPreTax = 0;
    }

    if (perUnitTax < 0) {
      perUnitTax = 0;
    }

    const baseUnitValue = taxIncluded ? basePreTaxPrice + baseTaxAmount : basePreTaxPrice;
    const finalUnitValue = taxIncluded ? perUnitPreTax + perUnitTax : perUnitPreTax;
    priceAdjustmentPerUnit = finalUnitValue - baseUnitValue;
  }

  const unitPrice = taxIncluded ? perUnitPreTax + perUnitTax : perUnitPreTax;

  return {
    unitPrice: Math.round(unitPrice),
    taxPerUnit: Math.round(perUnitTax),
    preTaxPerUnit: Math.round(perUnitPreTax),
    taxIncluded,
    priceAdjustmentPerUnit: Math.round(priceAdjustmentPerUnit),
  };
}

export function buildPreModifierOptionSnapshot(
  selection: ResolvedPreModifierSelection,
  pricing: ModifierPricingResult,
  quantity: number
): OrderedItemModifierPreModifierOption {
  const option = selection.option;
  const sanitizedQuantity = Math.max(1, normalizeNumber(quantity, 1));

  return {
    optionId: selection.optionId,
    name: option.name,
    posName: option.posName ?? null,
    kitchenName: option.kitchenName ?? null,
    displayMode: option.displayMode,
    multiplyPrice: !!option.multiplyPrice,
    multiplicationFactor: normalizeNumber(option.multiplicationFactor ?? 1, 1),
    fixedPrice: option.fixedPrice !== undefined ? normalizeNumber(option.fixedPrice) : undefined,
    color: option.color ?? null,
    priceAdjustment: pricing.priceAdjustmentPerUnit,
    totalPriceAdjustment: pricing.priceAdjustmentPerUnit * sanitizedQuantity,
  };
}
